import React, { useRef, useState } from 'react';
import { uploadFile } from '../../services/api';
import { Upload, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Media uploader — accepts BOTH images and videos.
 * Backend must accept the `gallery` category (or similar) that permits video.
 * Props:
 *  - category: e.g. 'gallery' (must allow video on the backend)
 *  - onUpload({ url, kind }): fired after each successful upload
 *  - label: optional button label
 *  - acceptVideo: boolean (default true)
 *  - maxSizeMB: front-side size cap warning (default 25)
 */
const MediaUploader = ({ category = 'gallery', onUpload, label = 'Görsel / Video Yükle', acceptVideo = true, maxSizeMB = 25 }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const pickFiles = () => inputRef.current?.click();

  const accept = acceptVideo
    ? 'image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime'
    : 'image/png,image/jpeg,image/webp,image/gif';

  const handleFiles = async (files) => {
    setError('');
    if (!files || files.length === 0) return;
    const f = files[0];
    if (f.size > maxSizeMB * 1024 * 1024) {
      setError(`Dosya ${maxSizeMB} MB'ı aşamaz`);
      return;
    }
    setBusy(true);
    try {
      const res = await uploadFile(category, f, (p) => setProgress(p));
      const absoluteUrl = `${BACKEND_URL}${res.data.url}`;
      onUpload?.({ url: absoluteUrl, kind: res.data.kind || 'image' });
    } catch (e) {
      setError(e.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={`media-uploader-input-${category}`}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <button
        type="button"
        onClick={pickFiles}
        disabled={busy}
        data-testid={`media-uploader-trigger-${category}`}
        className={`w-full flex items-center justify-center gap-2 border-2 border-dashed rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
          busy
            ? 'border-[#C8D843] bg-[#C8D843]/10 text-[#3D4A26] cursor-wait'
            : 'border-gray-300 hover:border-[#3D4A26] hover:bg-gray-50 text-gray-600'
        }`}
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            %{progress}
          </>
        ) : (
          <>
            <Upload size={14} />
            {label}
          </>
        )}
      </button>
      {error && (
        <p className="mt-1 text-[11px] text-red-500 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
};

export default MediaUploader;
